// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Panel from 'shared/components/common/Panel';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import GeneratePOsForLowStockMutation from 'main-app/mutations/GeneratePOsForLowStock';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

const WidgetContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  gap: 24px;
`;

const LargeNumber = styled.div`
  font-size: 64px;
  font-weight: 700;
  color: ${props => props.count > 0 ? '#c62828' : '#4caf50'};
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${props => props.count > 0 ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.count > 0 ? '#c62828' : '#4caf50'};
`;

const Description = styled.div`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
`;

class LowStockWidget extends PureComponent<Props> {
  handleGeneratePOs = () => {
    const confirmMessage = i18n.t(
      'This will generate purchase orders for all low stock items. Continue?'
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    GeneratePOsForLowStockMutation.commit(
      {},
      {
        onSuccess: response => {
          const count = response?.generatePOsForLowStock?.purchaseOrders?.length || 0;
          const itemsProcessed = response?.generatePOsForLowStock?.itemsProcessed || 0;
          
          if (count > 0) {
            Actions.alertNotification(
              i18n.t(`Successfully generated ${count} purchase order(s)!`),
              'success'
            );
          } else {
            Actions.alertNotification(
              i18n.t(`No purchase orders were generated. Items processed: ${itemsProcessed}. Check backend logs and vendor pricing.`),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate purchase orders: ${error.message}`),
            'error'
          );
        },
      }
    );
  };

  render() {
    const { history } = this.props;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        query={graphql`
          query LowStockWidgetQuery {
            lowStockItems {
              item {
                id
              }
            }
          }
        `}
        render={({ props, error }) => {
          const count = props?.lowStockItems?.length || 0;
          const hasLowStock = count > 0;

          return (
            <Panel
              title={i18n.t('Low Stock Alert')}
              style={{ marginBottom: 24 }}
            >
              <WidgetContent>
                <LargeNumber count={count}>
                  <IconWrapper count={count}>
                    <Icon
                      type={hasLowStock ? 'circle-alert-red' : 'circle-check-green'}
                      size={32}
                    />
                  </IconWrapper>
                  {count}
                </LargeNumber>
                
                <Description>
                  {hasLowStock
                    ? i18n.t('Items below safety quantity')
                    : i18n.t('All inventory levels are healthy')
                  }
                </Description>

                <ActionButtons>
                  <Button
                    buttonType="secondary"
                    onClick={() => history.push('/low-stock-items')}
                  >
                    {i18n.t('View Details')}
                  </Button>
                  {hasLowStock && (
                    <Button
                      buttonType="primary"
                      onClick={this.handleGeneratePOs}
                    >
                      {i18n.t('Auto-Generate POs')}
                    </Button>
                  )}
                </ActionButtons>
              </WidgetContent>
            </Panel>
          );
        }}
      />
    );
  }
}

export default LowStockWidget;
